import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, TrendingUp, TrendingDown, MoveRight, ChevronDown, ChevronRight, Activity } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OmCard, OmHead, OmKpi, Pill, GOLD, MUTED, NAVY, RED, GREEN, FIELD, chartTheme } from "@/components/om/OmBits";

const WINDOWS = [["rolling_30d", "30 days"], ["rolling_60d", "60 days"], ["rolling_90d", "90 days"]];
const num = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(1));

const Trend = ({ value }) => {
  const map = { Improving: [TrendingUp, GREEN], Degrading: [TrendingDown, RED], Stable: [MoveRight, GOLD] };
  const [Icon, color] = map[value] || map.Stable;
  return (
    <span className="inline-flex items-center gap-1 text-[11.5px]" style={{ color }} data-testid={`trend-${value}`}>
      <Icon className="w-3.5 h-3.5" /> {value}
    </span>
  );
};

export default function OmReliability() {
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [picked, setPicked] = useState([]);
  const [refDate, setRefDate] = useState(new Date().toISOString().slice(0, 10));
  const [openCards, setOpenCards] = useState({});
  const [busy, setBusy] = useState(false);

  const load = (refresh = false) => {
    setBusy(true);
    api.get("/om/reliability/metrics", { params: { asset_category: picked.join(","), reference_date: refDate, refresh } })
      .then(({ data }) => {
        setData(data);
        if (!cats.length) setCats(data.metrics.map((m) => m.asset_category));
        setOpenCards(Object.fromEntries(data.metrics.map((m, i) => [m.asset_category, i === 0])));
      })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setBusy(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refDate, picked.join(",")]);

  const summary = data?.summary;
  const kpis = useMemo(() => ([
    ["Best performing category", summary?.best_category || "—", summary?.best_mtbf_hours ? `${num(summary.best_mtbf_hours)} h MTBF` : "no failures yet"],
    ["Worst performing category", summary?.worst_category || "—", summary?.worst_mtbf_hours ? `${num(summary.worst_mtbf_hours)} h MTBF` : "—"],
    ["Avg MTTR — all categories", summary?.avg_mttr_hours_30d !== null && summary?.avg_mttr_hours_30d !== undefined ? `${num(summary.avg_mttr_hours_30d)} h` : "—", "rolling 30 days"],
    ["Unplanned failures this month", summary?.unplanned_failures_this_month ?? 0, "CM tickets raised"],
  ]), [summary]);

  return (
    <div data-testid="om-reliability-page">
      <OmHead title="Reliability Analytics" subtitle="MTBF and MTTR performance across all asset categories — rolling 30, 60, and 90-day windows.">
        <Button variant="outline" onClick={() => load(true)} disabled={busy} style={{ borderColor: `${GOLD}66`, color: GOLD, background: "transparent" }} data-testid="om-reliability-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </OmHead>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3" data-testid="om-reliability-kpis">
        {kpis.map(([label, value, sub]) => (
          <OmCard key={label} testid={`om-reliability-kpi-${label.split(" ")[0].toLowerCase()}`}>
            <div className="text-[10.5px] uppercase tracking-[0.14em]" style={{ color: MUTED }}>{label}</div>
            <div className="font-semibold mt-1 leading-tight" style={{ fontSize: 22, color: GOLD }}>{value}</div>
            <div className="text-[11px] mt-0.5" style={{ color: MUTED }}>{sub}</div>
          </OmCard>
        ))}
      </div>

      <OmCard className="mb-3" testid="om-reliability-controls">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>Reference date</Label>
            <Input type="date" value={refDate} onChange={(e) => setRefDate(e.target.value)} style={FIELD} data-testid="om-reliability-date" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <Label className="text-[11px] uppercase tracking-[0.1em]" style={{ color: MUTED }}>Asset categories</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              <button onClick={() => setPicked([])} className="px-2.5 py-1 text-[11.5px] border" data-testid="om-reliability-cat-all"
                style={{ borderRadius: 999, borderColor: picked.length ? `${GOLD}44` : GOLD, color: picked.length ? MUTED : NAVY, background: picked.length ? "transparent" : GOLD }}>
                All
              </button>
              {cats.map((c) => {
                const on = picked.includes(c);
                return (
                  <button key={c} onClick={() => setPicked(on ? picked.filter((x) => x !== c) : [...picked, c])}
                    className="px-2.5 py-1 text-[11.5px] border" data-testid={`om-reliability-cat-${c.toLowerCase()}`}
                    style={{ borderRadius: 999, borderColor: on ? GOLD : `${GOLD}44`, color: on ? NAVY : MUTED, background: on ? GOLD : "transparent" }}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </OmCard>

      {!data ? (
        <OmCard><p className="text-[12.5px]" style={{ color: MUTED }}>Calculating reliability metrics…</p></OmCard>
      ) : data.metrics.length === 0 ? (
        <OmCard testid="om-reliability-empty"><p className="text-[12.5px]" style={{ color: MUTED }}>No assets match this filter. Register assets, or load the starter register from the command centre.</p></OmCard>
      ) : (
        <div className="space-y-3">
          {data.metrics.map((m) => {
            const open = !!openCards[m.asset_category];
            const Chevron = open ? ChevronDown : ChevronRight;
            return (
              <OmCard key={m.asset_category} testid={`om-reliability-card-${m.asset_category.toLowerCase()}`}>
                <button onClick={() => setOpenCards({ ...openCards, [m.asset_category]: !open })}
                  className="w-full flex items-center justify-between gap-3 text-left" data-testid={`om-reliability-toggle-${m.asset_category.toLowerCase()}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Chevron className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
                    <h3 className="text-white font-semibold text-[15px] truncate">{m.asset_category}</h3>
                    <Pill tone="gold">{m.rolling_30d.asset_count} assets</Pill>
                    {m.rolling_30d.total_failures ? <Pill tone="bad">{m.rolling_30d.total_failures} failures / 30d</Pill> : <Pill tone="good">no failures / 30d</Pill>}
                  </div>
                  <div className="hidden sm:flex items-center gap-3">
                    <span className="text-[11.5px]" style={{ color: MUTED }}>MTBF <span style={{ color: GOLD }}>{num(m.rolling_30d.avg_mtbf_hours)} h</span></span>
                    <span className="text-[11.5px]" style={{ color: MUTED }}>MTTR <span style={{ color: GOLD }}>{num(m.rolling_30d.avg_mttr_hours)} h</span></span>
                  </div>
                </button>

                {open ? (
                  <div className="mt-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-[12.5px]" data-testid={`om-reliability-table-${m.asset_category.toLowerCase()}`}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${GOLD}38` }}>
                            <th className="text-left font-medium px-3 py-2 text-[10.5px] uppercase tracking-[0.1em]" style={{ color: GOLD }}>Metric</th>
                            {WINDOWS.map(([, l]) => (
                              <th key={l} className="text-left font-medium px-3 py-2 text-[10.5px] uppercase tracking-[0.1em]" style={{ color: GOLD }}>{l}</th>
                            ))}
                            <th className="text-left font-medium px-3 py-2 text-[10.5px] uppercase tracking-[0.1em]" style={{ color: GOLD }}>Trend</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[["MTBF (hours)", "avg_mtbf_hours", "mtbf_trend"], ["MTTR (hours)", "avg_mttr_hours", "mttr_trend"]].map(([label, field, tField]) => (
                            <tr key={label} className="text-white">
                              <td className="px-3 py-2.5">{label}</td>
                              {WINDOWS.map(([w]) => (
                                <td key={w} className="px-3 py-2.5 font-semibold tabular-nums" style={{ color: GOLD }}>{num(m[w][field])}</td>
                              ))}
                              <td className="px-3 py-2.5"><Trend value={m.rolling_30d[tField]} /></td>
                            </tr>
                          ))}
                          <tr style={{ color: MUTED }}>
                            <td className="px-3 py-2.5">Unplanned failures</td>
                            {WINDOWS.map(([w]) => <td key={w} className="px-3 py-2.5 tabular-nums">{m[w].total_failures}</td>)}
                            <td />
                          </tr>
                          <tr style={{ color: MUTED }}>
                            <td className="px-3 py-2.5">Corrective downtime (h)</td>
                            {WINDOWS.map(([w]) => <td key={w} className="px-3 py-2.5 tabular-nums">{num(m[w].total_downtime_hours)}</td>)}
                            <td />
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-5">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Activity className="w-3.5 h-3.5" style={{ color: GOLD }} />
                        <h4 className="text-white text-[13px] font-medium">{m.asset_category} Reliability Trend</h4>
                      </div>
                      <div style={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={m.weekly_trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke={chartTheme.grid} vertical={false} />
                            <XAxis dataKey="week" tick={{ fill: chartTheme.axis, fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                            <YAxis yAxisId="mtbf" tick={{ fill: GOLD, fontSize: 10 }} label={{ value: "MTBF (h)", angle: -90, position: "insideLeft", fill: GOLD, fontSize: 10 }} />
                            <YAxis yAxisId="mttr" orientation="right" tick={{ fill: RED, fontSize: 10 }} label={{ value: "MTTR (h)", angle: 90, position: "insideRight", fill: RED, fontSize: 10 }} />
                            <Tooltip contentStyle={chartTheme.tooltip} labelStyle={{ color: "#fff" }} />
                            <Legend wrapperStyle={{ fontSize: 11, color: MUTED }} />
                            <Line yAxisId="mtbf" type="monotone" dataKey="mtbf_hours" name="MTBF (h)" stroke={GOLD} strokeWidth={2} dot={false} />
                            <Line yAxisId="mttr" type="monotone" dataKey="mttr_hours" name="MTTR (h)" stroke={RED} strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                ) : null}
              </OmCard>
            );
          })}
        </div>
      )}
      {data ? (
        <p className="text-[11px] mt-3" style={{ color: MUTED }} data-testid="om-reliability-meta">
          Generated {new Date(data.generated_at).toLocaleString("en-IN")} · reference {data.reference_date}
          {data.cached ? " · served from the 1-hour cache" : ""}
        </p>
      ) : null}
    </div>
  );
}
